/**
 * CGM-FIELD-COMMISSIONING-R2 — pure parcel operational helpers.
 *
 * A parcel's physical valve state is derived ONLY from the exact channel of its
 * canonical controller node. Nothing here invents a state: an absent/ambiguous
 * channel value is UNKNOWN, a valid value with a stale node timestamp is STALE,
 * and only a fresh exact 0/1 is OPEN/CLOSED.
 *
 * Pure helpers only — no React, no network, no side effects.
 */

import type { IoTNode, ParcelValveBinding } from './iotApi';
import { deriveChannelValveStates } from './valveCommandSafety';
import { FRESHNESS_MS, nodeTimestamp } from './operationalOverview';

export type ParcelValveState = 'open' | 'closed' | 'unknown' | 'stale';

/**
 * A silent authorized valve may be saving power. This is only a possible
 * explanation, never proof of sleep or of the physical valve position.
 * Malformed/missing channel data and timestamps in a present node remain
 * unknown; authorization and command locks are handled separately.
 */
export function parcelMayBePowerSaving(
  binding: ParcelValveBinding,
  node: IoTNode | undefined,
  state: ParcelValveState,
): boolean {
  return binding.command_authorized
    && Boolean(binding.controller_node_id && binding.channel_id)
    && ((!node && state === 'unknown') || (Boolean(node) && state === 'stale'));
}

/**
 * Resolve a parcel's physical valve state from its controller node snapshot.
 *
 * Only the exact binding channel proves the parcel's valve. A missing node,
 * a missing/ambiguous channel measurement, or a missing timestamp is UNKNOWN;
 * a valid measurement with an over-age timestamp is STALE.
 */
export function parcelValveState(
  node: IoTNode | undefined,
  channelId: string,
  now: number,
): ParcelValveState {
  if (!node) return 'unknown';
  const state = deriveChannelValveStates(node).get(channelId);
  if (state !== 0 && state !== 1) return 'unknown';
  const ts = nodeTimestamp(node);
  if (ts === null) return 'unknown';
  if (now - ts >= FRESHNESS_MS) return 'stale';
  if (state === 1) return 'open';
  return 'closed';
}

/**
 * Parcel bindings whose canonical site + sector code resolve to the given
 * presentational (site, sectorCode). No farm-scoped fallback.
 */
export function parcelsFor(
  bindings: ParcelValveBinding[],
  siteName: string,
  sectorCode: string,
): ParcelValveBinding[] {
  return bindings.filter(
    (b) => b.site === siteName && b.sector_code === sectorCode,
  );
}

/** Whether any parcel binding in the set is CONFIRMED (installed assignment). */
export function hasConfirmedBinding(bindings: ParcelValveBinding[]): boolean {
  return bindings.some((b) => b.binding_status === 'CONFIRMED');
}

/** The actionable open/close availability for a parcel target (section 1). */
export interface ParcelControlAvailability {
  canOpen: boolean;
  canClose: boolean;
}

/**
 * Explicit OPEN/CLOSE intents do not require fresh telemetry. Sleeping nodes
 * may report infrequently; observation age is not a command authorization gate.
 * Binding/role authorization and in-flight locks remain. The canonical binding
 * supplies the controller identity even when a sleeping controller is absent
 * from the current telemetry inventory.
 */
export function parcelControlAvailability(
  _state: ParcelValveState,
  authorized: boolean,
  busy: boolean,
): ParcelControlAvailability {
  const available = authorized && !busy;
  return { canOpen: available, canClose: available };
}
